// Optional planning detail preserves the original worksheet keys and saved drafts.
// These are founder-entered records, never authenticated technical approvals.
const field = (id, label, type = "text", options) => ({ id, label, type, options });
const status = field("status", "Status", "select", ["Open", "In review", "Reviewed", "Not applicable"]);
export const REVIEW_COLUMNS = [
  field("item", "Requirement / decision"),
  status,
  field("owner", "Responsible owner"),
  field("due", "Due date", "date"),
  field("evidence", "Evidence reference / report location"),
  field("reviewer", "Named reviewer"),
  field("reviewed", "Review date", "date"),
  field("decision", "Review outcome / conditions"),
  field("rationale", "Not-applicable rationale"),
];

export const PLANNING_DETAILS = {
  "savory-profile-intake": {
    fields: [
      field("brief-sku", "Product / SKU and intended consumer"),
      field("brief-benchmark", "Benchmark product and intended use"),
      field("brief-constraints", "Must-have ingredients, prohibited ingredients and processing restrictions"),
      field("brief-targets", "Measurable sensory, nutrition, unit-cost and shelf-life targets"),
      field("brief-success", "Acceptance criteria, decision owners and required qualified reviews"),
      field("brief-regulatory", "Early regulatory questions, reviewer and next action"),
      field("brief-feasibility", "Early feasibility: equipment, minimum batch, filling and packaging constraints; contact, evidence and next action"),
    ],
  },
  "commercial-formula-method-sheet": {
    fields: [
      field("formula-batch", "Input batch mass and unit; expected sellable yield"),
      field("formula-reconcile", "Formula-to-cost check: every ingredient accounted for, matching batch size and units; explain exclusions"),
      field("formula-change", "Revision reason, prior version / document reference and affected testing, packaging, costing or labels"),
    ],
    table: { id: "supplier-records", title: "Ingredient and supplier qualification", columns: [
      field("ingredient", "Ingredient / sub-ingredients"), field("supplier", "Supplier and approved alternatives"),
      field("spec", "Specification, nutrition and allergen document references"),
      field("micro", "Microbial specification / treatment information, where applicable"),
      field("commercial", "MOQ, price tiers, lead time and quote date"), status,
      field("review", "Qualification reviewer, date and decision / next action"),
    ] },
  },
  "first-run-costing-worksheet": {
    fields: [
      field("development-costs", "One-time development expenses (separate from recurring manufacturing COGS)"),
      field("recurring-tests", "Recurring production / testing costs and allocation per batch"),
      field("selling-costs", "Selling and distribution expenses (separate from manufacturing COGS)"),
    ],
  },
  "pricing-margin-workbook": {
    fields: [
      field("cost-basis", "Current estimate basis", "select", ["Initial estimate", "Co-manufacturer quote", "Pilot-confirmed estimate", "Commercial actuals"]),
      field("cost-source", "Price / yield sources, dates and assumptions; confirm all formula ingredients are included"),
      field("cost-development", "One-time development budget (not included in recurring unit cost)"),
    ],
    table: { id: "cost-revisions", title: "Cost estimate history — record each review before replacing inputs", columns: [
      field("date", "Date", "date"), field("basis", "Basis", "select", ["Initial estimate", "Co-manufacturer quote", "Pilot-confirmed estimate", "Commercial actuals"]),
      field("version", "Formula / package version"), field("yield", "Sellable yield and units"),
      field("cogs", "Manufacturing COGS per unit and currency"), field("selling", "Selling expense per unit"),
      field("source", "Quote / pilot / production evidence"), field("review", "Reviewer, decision and next action"),
    ] },
  },
  "testing-brief": {
    fields: [
      field("test-phase", "Testing stage", "select", ["Bench development", "Pilot testing", "Launch-supporting verification"]),
      field("test-representative", "How samples represent the intended commercial process, package and storage; limitations"),
      field("test-shelf-life", "Shelf-life protocol: conditions, time points, sample counts, acceptance criteria and qualified reviewer"),
    ],
    table: { id: "test-plans", title: "Testing plan and results", columns: [
      field("analysis", "Analysis / question"), field("method", "Method and acceptance criteria"),
      field("lab", "Laboratory"), field("samples", "Sample quantity, handling and shipping"),
      field("cost", "Cost / currency"), field("turnaround", "Turnaround and due date"),
      field("report", "Report reference, result and limitations"), field("review", "Reviewer, decision and date"),
    ] },
  },
  "packaging-brief": {
    fields: [
      field("package-feasibility", "Early supplier / co-man feasibility: equipment, fill conditions, minimum quantities and confirmation evidence"),
      field("package-test", "Compatibility / shelf-life testing, representative sample and review decision"),
    ],
  },
  "regulatory-triage-worksheet": {
    fields: [
      field("reg-review", "Qualified reviewer, review date, jurisdiction and reviewed formula / label / process versions"),
      field("reg-outcome", "Review findings, required changes, supporting report and outstanding conditions"),
    ],
  },
  "launch-readiness-checklist": {
    fields: [
      field("launch-plan", "Launch channels, timing, responsible people and promotional activities"),
      field("launch-education", "Product education, preparation directions and recipe / usage links"),
      field("launch-feedback", "Consumer feedback collection method and survey / QR destination URL"),
      field("launch-review-date", "Scheduled post-launch review", "date"),
      field("launch-review-owner", "Post-launch review owner and technical escalation contact"),
    ],
    table: { id: "post-launch-reviews", title: "Post-launch measures and corrective actions", columns: [
      field("period", "Review period"), field("sales", "Sales and repeat purchases"),
      field("quality", "Complaints, returns and safety / quality concerns"),
      field("margin", "Actual margin and cost assumptions"), field("feedback", "Consumer feedback / evidence"),
      field("action", "Corrective action / improvement"), field("owner", "Owner"),
      field("due", "Due date", "date"), status,
    ] },
  },
};

export function detailTables(templateId) {
  const specific = PLANNING_DETAILS[templateId]?.table;
  return [...(specific ? [specific] : []), {
    id: "review-records", title: "Requirement and review evidence — self-reported",
    columns: REVIEW_COLUMNS,
  }];
}

export function detailExportRows(templateId, values) {
  const rows = [];
  for (const f of PLANNING_DETAILS[templateId]?.fields || []) {
    if (values[f.id]) rows.push([f.label, values[f.id]]);
  }
  for (const table of detailTables(templateId)) {
    const records = Array.isArray(values[table.id]) ? values[table.id] : [];
    if (!records.length) continue;
    rows.push([table.title], table.columns.map((c) => c.label));
    records.forEach((r) => rows.push(table.columns.map((c) => r[c.id] || "")));
  }
  if (rows.length) rows.unshift(["Planning detail — self-reported, not professional approval"]);
  return rows;
}

export function reviewIssues(values = {}) {
  const records = Array.isArray(values["review-records"]) ? values["review-records"] : [];
  return records.flatMap((r, i) => {
    if (r.status === "Not applicable" && !r.rationale?.trim()) {
      return [`Review item ${i + 1}: explain why it is not applicable. Safety or regulatory exclusions require qualified review.`];
    }
    if (r.status === "Reviewed" && (!r.reviewer?.trim() || !r.reviewed || !r.evidence?.trim() || !r.decision?.trim())) {
      return [`Review item ${i + 1}: record the reviewer, review date, evidence and outcome.`];
    }
    return [];
  });
}